/**
 * Hook to restore widget pagination states from cache on page load
 * Should be called before widgets render to ensure they initialize with cached state
 */

import { useEffect } from 'react'
import { useStore } from 'zustand'

import { usePageStateStore } from '@src/components/pl-base-widget/contexts/page-state-context'
import type { PLPageMeta } from '@src/components/pl-page-context'
import { getCollectionCacheKey } from '@src/pages/[...slug]/components/collection/utils'
import collectionCacheStateManager from '@src/quince-site-common/src/redux/globalStateManager/CollectionCacheStateManager'

/**
 * Hook to restore widget states from cache on page load
 * Should be used in CLP page components before widgets render
 * @param pageMeta - Page metadata (can be passed as prop or from context)
 */
export function useCLPWidgetStateRestoration(pageMeta: PLPageMeta) {
  const pageStateStore = usePageStateStore()
  const restoreWidgetStates = useStore(pageStateStore, state => state.widgetStateActions.restoreWidgetStates)

  // Generate cache key
  const cacheKey = getCollectionCacheKey(pageMeta.pathname, pageMeta.searchParams)

  useEffect(() => {
    // Get cached data
    const cachedData = collectionCacheStateManager.getCollectionCache(cacheKey)

    if (cachedData?.widgetPaginationStates) {
      // Restore widget states to store
      restoreWidgetStates(cachedData.widgetPaginationStates)
    }
  }, [cacheKey, restoreWidgetStates])
}
